import { button, element, heading } from "@groundspark/browser-ui";
import type { Leaflet } from "./domain";
import { destinationField, qrDestination } from "./leaflet-destination";
import { type PaperSize, paperSizeLabels, paperSizes } from "./leaflet-pages";

const paperSizeDescriptions: Readonly<Record<PaperSize, string>> = {
  a5: "148 × 210 mm",
  a4: "210 × 297 mm",
};

/**
 * Asks for a name, paper size and QR code destination, then creates the leaflet.
 * Creation errors stay in the dialog; aborting the signal closes it. Resolves
 * `null` when cancelled.
 */
export function newLeafletDialog(
  create: (name: string, paperSize: PaperSize, destination: string) => Promise<Leaflet>,
  signal: AbortSignal,
): Promise<Leaflet | null> {
  const dialog = element("dialog", "dialog");
  const title = heading(2, "New leaflet", "dialog__title");
  title.id = "new-leaflet-title";
  dialog.setAttribute("aria-labelledby", title.id);
  const name = element("input", "field__control");
  name.name = "name";
  name.required = true;
  name.maxLength = 200;
  name.autocomplete = "off";
  const sizes = element(
    "fieldset",
    "check-group",
    element("legend", "field__label", "Paper size"),
  );
  for (const size of paperSizes) {
    const option = element("input", "check-row__input");
    option.type = "radio";
    option.name = "paper_size";
    option.value = size;
    option.checked = size === "a5";
    sizes.append(
      element(
        "label",
        "check-row",
        option,
        element("span", null, `${paperSizeLabels[size]} (${paperSizeDescriptions[size]})`),
      ),
    );
  }
  const destination = destinationField("");
  const error = element("p", "composer__error");
  error.setAttribute("role", "alert");
  const cancel = button("Cancel", "button button--white", () => dialog.close());
  const submit = element("button", "button button--dark-blue", "Create leaflet");
  submit.type = "submit";
  const form = element(
    "form",
    null,
    element("label", "field", element("span", "field__label", "Name"), name),
    sizes,
    destination.label,
    error,
    element("div", "dialog__actions", cancel, submit),
  );
  dialog.append(element("header", "dialog__header", title), form);
  const previousFocus = document.activeElement;
  let creating = false;
  let created: Leaflet | null = null;
  return new Promise((resolve) => {
    const abort = (): void => dialog.close();
    signal.addEventListener("abort", abort, { once: true });
    dialog.addEventListener("cancel", (event) => {
      if (creating) event.preventDefault();
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (creating) return;
      const value = name.value.trim();
      if (value === "") {
        error.textContent = "Enter a name for the leaflet.";
        name.focus();
        return;
      }
      const paperSize = (form.querySelector<HTMLInputElement>(
        'input[name="paper_size"]:checked',
      )?.value ?? "a5") as PaperSize;
      let address: string;
      try {
        address = qrDestination(destination.input.value);
      } catch (failure) {
        error.textContent = failure instanceof Error ? failure.message : String(failure);
        destination.input.focus();
        return;
      }
      creating = true;
      submit.disabled = true;
      cancel.disabled = true;
      error.textContent = "";
      void create(value, paperSize, address)
        .then((leaflet) => {
          created = leaflet;
          dialog.close();
        })
        .catch((failure: unknown) => {
          error.textContent = failure instanceof Error ? failure.message : String(failure);
        })
        .finally(() => {
          creating = false;
          submit.disabled = false;
          cancel.disabled = false;
        });
    });
    dialog.addEventListener(
      "close",
      () => {
        signal.removeEventListener("abort", abort);
        dialog.remove();
        if (
          created === null &&
          previousFocus instanceof HTMLElement &&
          previousFocus.isConnected
        )
          previousFocus.focus({ preventScroll: true });
        resolve(created);
      },
      { once: true },
    );
    document.body.append(dialog);
    dialog.showModal();
    name.focus();
  });
}
